import React, { useEffect, useRef, useState } from "react";
import { AssistantMessageProps } from "@copilotkit/react-ui";
import AwesomeIcon from "../../AwesomeIcon";
import {
  faSpinner,
  faThumbsUp,
  faThumbsDown,
  faCopy,
  faArrowsRotate,
  faStars,
} from "@fortawesome/pro-regular-svg-icons";
import { faMessageBot } from "@fortawesome/pro-duotone-svg-icons";
import PatientSummary from "../PatientSummary";
import { useCopilotChat } from "@copilotkit/react-core";
import { Role, TextMessage } from "@copilotkit/runtime-client-gql";
import { trackEvent } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../utils/mixpanelEvents";

const AssistantMessageHeader = () => {
  return (
    <div className="flex items-center gap-x-2 rounded-t-lg px-4 py-2 text-md w-fit font-semibold text-teal-200 dark:text-teal-300 bg-gray-500 dark:bg-gray-800">
      <AwesomeIcon
        name={faStars}
        size="lg"
        className="text-teal-200 dark:text-teal-300"
      />
      AI Assistant
    </div>
  );
};

const FeedbackControls: React.FC<{
  message: string;
  onThumbsUp?: (msg: string) => void;
  onThumbsDown?: (msg: string) => void;
  onCopy?: (msg: string) => void;
  onRegenerate?: () => void;
  showCopy?: boolean;
  showRegenerate?: boolean;
  iconClassName?: string;
  centerCard?: boolean;
}> = ({
  message,
  onThumbsUp,
  onThumbsDown,
  onCopy,
  onRegenerate,
  showCopy = true,
  showRegenerate = true,
  iconClassName = "",
  centerCard = false,
}) => {
  const [feedback, setFeedback] = useState<string>("");
  const feedbackTimeout = useRef<NodeJS.Timeout | null>(null);

  const showFeedback = (msg: string) => {
    setFeedback(msg);
    if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current);
    feedbackTimeout.current = setTimeout(() => setFeedback(""), 1800);
  };

  const controls = (
    <div
      className={
        centerCard
          ? "flex flex-row space-x-6 items-center justify-center"
          : "flex space-x-4 items-center border-t border-gray-400/20 mt-3 pt-2 w-full"
      }
    >
      <button
        aria-label="Thumbs Up"
        className={
          "hover:text-teal-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[-8deg]"
        }
        onClick={() => {
          onThumbsUp && onThumbsUp(message);
          trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, { action: "thumbs_up" });
          showFeedback("Thank you for your feedback!");
        }}
        type="button"
      >
        <AwesomeIcon name={faThumbsUp} size="sm" className={iconClassName} />
      </button>
      <button
        aria-label="Thumbs Down"
        className={
          "hover:text-red-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[8deg]"
        }
        onClick={() => {
          onThumbsDown && onThumbsDown(message);
          trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, { action: "thumbs_down" });
          showFeedback("Thank you for your feedback!");
        }}
        type="button"
      >
        <AwesomeIcon name={faThumbsDown} size="sm" className={iconClassName} />
      </button>
      {showCopy && (
        <button
          aria-label="Copy Message"
          className={
            "hover:text-teal-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[-6deg]"
          }
          onClick={() => {
            onCopy && onCopy(message);
            if (message) {
              navigator.clipboard?.writeText(message);
            }
            trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, { action: "copy" });
            showFeedback("Copied!");
          }}
          type="button"
        >
          <AwesomeIcon name={faCopy} size="sm" className={iconClassName} />
        </button>
      )}
      {showRegenerate && (
        <button
          aria-label="Regenerate Message"
          className={
            "hover:text-teal-400 focus:outline-none transition-transform transition-colors duration-150 ease-in-out hover:scale-110 hover:-translate-y-0.5 hover:rotate-[6deg]"
          }
          onClick={() => {
            onRegenerate && onRegenerate();
            trackEvent(MIXPANEL_EVENTS.ASSISTANT_FEEDBACK, { action: "regenerate" });
            showFeedback("Regenerating...");
          }}
          type="button"
        >
          <AwesomeIcon name={faArrowsRotate} size="sm" className={iconClassName} />
        </button>
      )}
      {feedback && (
        <span
          className="ml-2 text-teal-300 dark:text-teal-200 animate-fade-in-out text-sm font-medium"
          style={{ minWidth: 80 }}
        >
          {feedback}
        </span>
      )}
    </div>
  );
  if (centerCard) {
    return (
      <div className="flex flex-col items-start w-full">
        <div className="bg-gray-50 dark:bg-gray-800/40 transition-colors rounded-md py-2 px-4 mt-3 w-fit flex flex-col items-start">
          {controls}
        </div>
      </div>
    );
  }
  return controls;
};

const CustomAssistantMessage: React.FC<AssistantMessageProps> = (props) => {
  const {
    message,
    isLoading,
    subComponent,
    onThumbsUp,
    onThumbsDown,
    onCopy,
    onRegenerate,
  } = props;
  const loadingKeys = [
    "Analyzing clinical context...",
    "Compiling patient data...",
    "Processing medical records...",
    "Interpreting inputs...",
    "Reviewing patient information...",
    "Gathering relevant details...",
    "Synchronizing with medical database...",
  ];
  const currentIndex = Math.floor(Math.random() * loadingKeys.length);

  const isPatientSummary = message?.includes("<<<PATIENT_SUMMARY_JSON>>>");

  const { visibleMessages, setMessages, appendMessage } = useCopilotChat();
  useEffect(() => {
    if (
      visibleMessages[visibleMessages.length - 1]?.content ===
      "❌ An error occurred. Please try again."
    ) {
      setMessages(visibleMessages.slice(0, -1));
      setTimeout(() => {
        appendMessage(
          new TextMessage({
            content: `Please start a new chat to continue.`,
            role: Role.Assistant,
          }),
          { followUp: false }
        );
      }, 1000);
    }
  }, [visibleMessages]);

  return (
    <>
      {(message || subComponent) && (
        <div className="flex items-start mb-4 last:mb-0 justify-start">
          {/* Render content and feedback controls according to type */}
          <div className="max-w-[90%] w-full">
            {isPatientSummary ? (
              <>
                <AssistantMessageHeader />
                <PatientSummary message={message ?? ""} />
                <FeedbackControls
                  message={message ?? ""}
                  onThumbsUp={onThumbsUp}
                  onThumbsDown={onThumbsDown}
                  showCopy={false}
                  showRegenerate={false}
                  iconClassName="text-gray-700 dark:text-gray-200 hover:text-inherit dark:hover:text-inherit"
                  centerCard={true}
                />
              </>
            ) : subComponent ? (
              <>
                <AssistantMessageHeader />
                {subComponent}
                <FeedbackControls
                  message={message ?? ""}
                  onThumbsUp={onThumbsUp}
                  onThumbsDown={onThumbsDown}
                  showCopy={false}
                  showRegenerate={false}
                  iconClassName="text-gray-700 dark:text-gray-200 hover:text-inherit dark:hover:text-inherit"
                  centerCard={true}
                />
              </>
            ) : (
              <div className="inline-block">
                <AssistantMessageHeader />
                <div className="rounded-b-lg px-5 py-4 text-gray-700 dark:text-white bg-gray-100 dark:bg-gray-800">
                  <p className="whitespace-pre-wrap">{message}</p>
                  <FeedbackControls
                    message={message ?? ""}
                    onThumbsUp={onThumbsUp}
                    onThumbsDown={onThumbsDown}
                    onCopy={onCopy}
                    onRegenerate={onRegenerate}
                    showCopy={true}
                    showRegenerate={true}
                    iconClassName=""
                    centerCard={false}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {isLoading && (
        <div className="flex space-x-3 dark:text-gray-400 text-gray-600 pl-8">
          <AwesomeIcon name={faSpinner} size="lg" className="animate-spin" />
          <p className="text-md italic">{loadingKeys[currentIndex]}</p>
        </div>
      )}
    </>
  );
};

export default CustomAssistantMessage;
